import React, { useMemo } from 'react';
import { MdAdd } from 'react-icons/md';
import { isDisabled } from 'app/infrastructure/data/date';
import { useCustomWidths, useWidthObserver } from 'app/components/resize-hooks';

import { getNextElement, getPrevElement } from 'app/area/shared/saved-reports/utils/get-saved-report';
import { SavedReportsValidation } from 'app/area/shared/saved-reports/types-store';
import { UpdateSavedReportHandler } from 'app/area/shared/saved-reports/types';
import { SavedReportDashboard } from '../../common/types-store';
import { DashboardAdminTranslation } from '../../constants';
import { defaultWidth } from '../../constants';
import { DashboardSavedReportsState } from '../types-store';
import { SavedReportsFavourite } from './saved-reports-favourite';

import styles from './saved-reports-list.scss';

interface SavedReportsListProps {
  savedReportsState: DashboardSavedReportsState;

  editSavedReportById: (savedReport: SavedReportDashboard) => void;
  editSavedReports: (savedReport: SavedReportDashboard) => void;
  updateSavedReport: UpdateSavedReportHandler<SavedReportDashboard>;
  deleteSavedReport: (savedReportId: number) => void;

  onAddReport: () => void;
  onAddReportById: () => void;

  validation: SavedReportsValidation<SavedReportDashboard>;
  translation: DashboardAdminTranslation;
}
export const SavedReportsList: React.FC<SavedReportsListProps> = ({
  savedReportsState, editSavedReportById, editSavedReports, updateSavedReport, deleteSavedReport,
  onAddReport, onAddReportById, validation, translation
}) => {

  const { savedReports, displayDisabled } = savedReportsState;

  const { width, ref: observedRef } = useWidthObserver<HTMLDivElement>();

  const widthItems = useMemo(() =>
    savedReports?.map(savedReport => ({
      id: savedReport.code?.value,
      width: displayDisabled || !isDisabled(savedReport.disabled) ? savedReport.minWidth : 0
    })), [savedReports, displayDisabled]);

  const { minWidths, requestCustomWidth } = useCustomWidths(widthItems, width ?? defaultWidth);

  const onRequestCustomWidth = (id: number, value: number) => {
    const savedReport = savedReports.find(report => report.code?.value === id);
    if (displayDisabled || (savedReport && !isDisabled(savedReport?.disabled))) {
      requestCustomWidth(id, value);
    }
  };

  return <div className={styles.dashboardLayout} ref={observedRef}>
    {savedReports?.map(
      (savedReport, index) => {
        const savedReportWidth = minWidths.find(
          elementWidth => elementWidth.id === savedReport.code?.value
        );

        const prevElement = getPrevElement(savedReports, index, displayDisabled);
        const nextElement = getNextElement(savedReports, index, displayDisabled);

        return <SavedReportsFavourite
          key={savedReport.code?.value}
          savedReport={savedReport}
          containerWidth={savedReportWidth?.width}
          prevElementSequence={prevElement?.sequence}
          nextElementSequence={nextElement?.sequence}
          editSavedReportById={editSavedReportById}
          editSavedReports={editSavedReports}
          updateSavedReport={updateSavedReport}
          deleteSavedReport={deleteSavedReport}
          requestCustomWidth={onRequestCustomWidth}
          display={displayDisabled || !isDisabled(savedReport?.disabled)}
          validation={validation?.[savedReport.code?.value]}
          translation={translation} />;
      }
    )}
    <div className={styles.connect} onClick={onAddReport}>
      {translation.add_reports}<br />
      <MdAdd size={32} />
    </div>
    <div className={styles.connect} onClick={onAddReportById}>
      {translation.add_report_by_id}<br />
      <MdAdd size={32} />
    </div>
  </div >;
};
